import React from 'react'
import { useSignal } from '@preact/signals-react'
import { Autocomplete, Grid, Skeleton, TextField } from '@mui/material'
import { AmchartSeeker } from '@ui-seeker/core'
import { isOneWayLoading, oneWayData, oneWayPriceMode } from 'Pages/AnalyzersV2/OneWay/@signals'
import { oneWayAxisLabel } from 'Pages/AnalyzersV2/OneWay/Table/axis'

const SERIES_COLORS = ['#42a5f5', '#66bb6a', '#ffa726', '#ef5350', '#ab47bc', '#26c6da', '#ff7043', '#8d6e63']

export default function OneWayChart() {
	const { results, headers, subHeaders, subColumns } = oneWayData.value || {}

	const selectedRow = useSignal(null)
	const selectedLor = useSignal(null)

	if (isOneWayLoading.value) return <Skeleton variant="rectangular" height={400} />
	if (!results?.length || !headers?.length) return null

	// the rows are webs or brands depending on the toggle the response was built with
	const axisLabel = oneWayAxisLabel()
	const rowNames = results.map(r => r.name)
	const rowName = rowNames.includes(selectedRow.value) ? selectedRow.value : rowNames[0]
	const lor = subHeaders?.includes(selectedLor.value) ? selectedLor.value : subHeaders?.[0] ?? null

	const row = results.find(r => r.name === rowName)

	const priceField = oneWayPriceMode.value === 'roundTrip' ? 'oneWayPrice' : 'fee'

	const chartData = headers.map(date => {
		const point = { date }
		subColumns?.forEach(acriss => {
			const entry = row?.[date]?.[String(lor)]?.find(e => e.acriss === acriss)
			const price = entry?.[priceField]
			point[acriss] = (price != null && price !== 'N/A') ? parseFloat(price) : null
		})
		return point
	})

	const keySeries = (subColumns || []).map((acriss, i) => ({
		dataKey: acriss,
		color: SERIES_COLORS[i % SERIES_COLORS.length],
	}))

	return <AmchartSeeker
		rootID="OneWayFeeChart"
		title="One Way Fee Evolution"
		data={chartData}
		series={keySeries.map(({ dataKey, color }) => ({
			name: dataKey,
			yField: dataKey,
			xField: 'date',
			color,
			connect: true,
			type: 'line',
			tooltip: { pointerOrientation: 'horizontal' },
			tension: 1,
		}))}
		sx={{ height: 450 }}
		cardProps={{
			actions: <Grid container spacing={2} sx={{ pr: 2 }}>
				<Grid item>
					<Autocomplete
						size="small"
						options={rowNames}
						value={rowName}
						onChange={(_, v) => { if (v) selectedRow.value = v }}
						disableClearable
						sx={{ minWidth: 220 }}
						renderInput={params => <TextField {...params} label={axisLabel} />}
					/>
				</Grid>
				<Grid item>
					<Autocomplete
						size="small"
						options={subHeaders || []}
						getOptionLabel={o => `${o}`}
						isOptionEqualToValue={(o, v) => String(o) === String(v)}
						value={lor}
						onChange={(_, v) => { if (v != null) selectedLor.value = v }}
						disableClearable
						sx={{ minWidth: 100 }}
						renderInput={params => <TextField {...params} label="LOR" />}
					/>
				</Grid>
			</Grid>
		}}
		chartProps={{
			showBullets: true,
			enabledPDF: false,
			numberFormat: '#,###.00',
			dateFormatReceived: 'yyyyMMdd',
			scrollbarY: { show: true },
			xAxis: {
				categoryField: 'date',
				isDateAxis: true,
				dateFormat: 'dd MMM yyyy (EEE)',
				title: 'Pickup Date',
				baseInterval: { timeUnit: 'day', count: 1 },
				labelProps: { rotation: '10' },
			},
			yAxis: { title: oneWayPriceMode.value === 'roundTrip' ? 'One Way Price' : 'Fee' },
		}}
	/>
}
